import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  message,
} from "antd";
import { Client } from "../../types/client.type";
import { ClientSpool, Spool } from "../../types/spool.type";
import { Currency } from "../../types/currency.type";
import { Cashbox } from "../../types/cashbox.type";
import { spoolApi } from "../../api/spoolApi";
import { useAuth } from "../../contexts/auth.context";
import { useExchangeRate } from "../../contexts/exchangeRate.context";
import useGetRequest from "../../hooks/useGetRequest";
import { canPaySpools } from "../../utils/roleUtils";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";

const { Option } = Select;

interface Props {
  open: boolean;
  client: Client | null;
  onClose: () => void;
  onChanged?: () => void;
}

interface PayFormValues {
  quantity: number;
  cashboxId?: string;
  exchangeRate: number;
  description?: string;
}

// Та же конвертация, что и на бэке (utils/convertCurrency) — только для предпросмотра суммы
const convert = (amount: number, from: Currency, to: Currency, rate: number) => {
  if (from === to) return amount;
  return from === Currency.US_DOLLAR ? amount * rate : amount / rate;
};

const ClientSpoolsModal: React.FC<Props> = ({ open, client, onClose, onChanged }) => {
  const { authState } = useAuth();
  const { exchangeRate } = useExchangeRate();
  const canPay = canPaySpools(authState.user?.role);

  const [catalog, setCatalog] = useState<Spool[]>([]);
  const [holdings, setHoldings] = useState<ClientSpool[]>([]);
  const [loading, setLoading] = useState(false);
  const [giveSpoolId, setGiveSpoolId] = useState<string | undefined>(undefined);
  const [giveQty, setGiveQty] = useState<number>(1);
  const [returnQty, setReturnQty] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);

  const [payHolding, setPayHolding] = useState<ClientSpool | null>(null);
  const [payForm] = Form.useForm<PayFormValues>();
  const [payValues, setPayValues] = useState<Partial<PayFormValues>>({});

  const { data: cashboxesList, loading: cashboxesLoading } = useGetRequest("/cashbox/list");

  const currency = (client?.debtCurrency as Currency) || Currency.US_DOLLAR;

  const load = async () => {
    if (!client) return;
    setLoading(true);
    try {
      const [cat, hold] = await Promise.all([
        spoolApi.getSpools(true),
        spoolApi.getClientSpools(client._id),
      ]);
      setCatalog(cat);
      setHoldings(hold);
    } catch (error) {
      message.error("Ошибка при загрузке катушек");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && client) {
      setGiveSpoolId(undefined);
      setGiveQty(1);
      setReturnQty({});
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client?._id]);

  const handleGive = async () => {
    if (!client || !giveSpoolId) {
      message.warning("Выберите катушку");
      return;
    }
    setSubmitting(true);
    try {
      await spoolApi.giveSpool({ clientId: client._id, spoolId: giveSpoolId, quantity: giveQty });
      message.success("Катушки выданы клиенту");
      setGiveSpoolId(undefined);
      setGiveQty(1);
      await load();
      onChanged?.();
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Ошибка при выдаче");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturn = async (holding: ClientSpool) => {
    if (!client) return;
    const qty = returnQty[holding._id] || holding.quantity;
    setSubmitting(true);
    try {
      await spoolApi.returnSpool({ clientId: client._id, spoolId: holding.spoolId, quantity: qty });
      message.success("Возврат принят");
      await load();
      onChanged?.();
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Ошибка при возврате");
    } finally {
      setSubmitting(false);
    }
  };

  // Форма живёт внутри модалки с destroyOnClose, поэтому значения задаём
  // через initialValues + key, а не setFieldsValue (поля ещё не смонтированы)
  const buildPayInitial = (holding: ClientSpool): PayFormValues => ({
    quantity: returnQty[holding._id] || holding.quantity,
    cashboxId: undefined,
    exchangeRate: exchangeRate || 0,
    description: undefined,
  });

  const openPay = (holding: ClientSpool) => {
    setPayHolding(holding);
    setPayValues(buildPayInitial(holding));
  };

  const closePay = () => {
    setPayHolding(null);
    setPayValues({});
    payForm.resetFields();
  };

  const handlePay = async (values: PayFormValues) => {
    if (!client || !payHolding || !values.cashboxId) return;
    setSubmitting(true);
    try {
      await spoolApi.paySpool({
        clientId: client._id,
        spoolId: payHolding.spoolId,
        quantity: values.quantity,
        cashboxId: values.cashboxId,
        exchangeRate: values.exchangeRate,
        description: values.description,
      });
      message.success("Оплата за катушки принята");
      closePay();
      await load();
      onChanged?.();
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Ошибка при оплате");
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (v: number) => (v ?? 0).toLocaleString("ru-RU");

  const totalSpoolDebt = holdings.reduce((sum, h) => sum + (h.depositAmount || 0), 0);

  // Списываемая часть залога — та же пропорция, что считает бэк
  const payDebtAmount = (() => {
    if (!payHolding) return 0;
    const qty = payValues.quantity || 0;
    if (qty <= 0 || qty > payHolding.quantity) return 0;
    return qty === payHolding.quantity
      ? payHolding.depositAmount
      : roundToThreeDecimals((payHolding.depositAmount * qty) / payHolding.quantity);
  })();

  const payCashbox: Cashbox | undefined = cashboxesList?.find(
    (c: Cashbox) => c._id === payValues.cashboxId
  );

  const payCashAmount = payCashbox
    ? roundToThreeDecimals(
        convert(payDebtAmount, currency, payCashbox.currency, payValues.exchangeRate || 0)
      )
    : 0;

  const holdingColumns = [
    { title: "Катушка", dataIndex: "spoolName", key: "spoolName" },
    { title: "На руках", dataIndex: "quantity", key: "quantity" },
    {
      title: `Залог (${currency})`,
      dataIndex: "depositAmount",
      key: "depositAmount",
      render: (v: number) => fmt(v),
    },
    {
      title: "Действие",
      key: "action",
      render: (_: any, h: ClientSpool) => (
        <Space>
          <InputNumber
            min={1}
            max={h.quantity}
            value={returnQty[h._id] ?? h.quantity}
            onChange={(val) => setReturnQty((prev) => ({ ...prev, [h._id]: Number(val) || 1 }))}
            style={{ width: 80 }}
          />
          <Button type="primary" size="small" loading={submitting} onClick={() => handleReturn(h)}>
            Принять возврат
          </Button>
          {canPay && (
            <Button size="small" disabled={submitting} onClick={() => openPay(h)}>
              Оплатил
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const selectedSpool = catalog.find((s) => s._id === giveSpoolId);
  const giveUnitPrice = selectedSpool ? selectedSpool.price[currency] : 0;

  return (
    <>
      <Modal
        title={`Катушки клиента${client ? `: ${client.name}` : ""}`}
        open={open}
        onCancel={onClose}
        footer={[
          <Button key="close" onClick={onClose}>
            Закрыть
          </Button>,
        ]}
        width={780}
      >
        <Divider orientation="left" style={{ marginTop: 0 }}>
          На руках у клиента
        </Divider>
        {holdings.length === 0 ? (
          <Empty description="Катушек на руках нет" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table
            size="small"
            loading={loading}
            columns={holdingColumns as any}
            dataSource={holdings}
            rowKey={(r) => r._id}
            pagination={false}
          />
        )}

        <div style={{ marginTop: 8, fontWeight: "bold" }}>
          Долг катушек: {fmt(totalSpoolDebt)} {currency}
        </div>

        <Divider orientation="left">Выдать катушку</Divider>
        <Space wrap align="end">
          <div>
            <div style={{ marginBottom: 4 }}>Вид катушки</div>
            <Select
              style={{ width: 280 }}
              placeholder="Выберите катушку"
              value={giveSpoolId}
              onChange={setGiveSpoolId}
              showSearch
              optionFilterProp="children"
              loading={loading}
            >
              {catalog.map((s) => (
                <Option key={s._id} value={s._id}>
                  {s.name} — {fmt(s.price[currency])} {currency} (склад: {s.stock})
                </Option>
              ))}
            </Select>
          </div>
          <div>
            <div style={{ marginBottom: 4 }}>Количество</div>
            <InputNumber
              min={1}
              value={giveQty}
              onChange={(v) => setGiveQty(Number(v) || 1)}
              style={{ width: 100 }}
            />
          </div>
          <Button type="primary" loading={submitting} onClick={handleGive}>
            Выдать
          </Button>
        </Space>
        {selectedSpool && (
          <div style={{ marginTop: 8, color: "#888" }}>
            Долг клиента вырастет на {fmt(giveUnitPrice * giveQty)} {currency}
          </div>
        )}
      </Modal>

      <Modal
        title={`Оплата катушки${payHolding ? `: ${payHolding.spoolName}` : ""}`}
        open={!!payHolding}
        onCancel={closePay}
        footer={null}
        width={520}
        destroyOnClose
      >
        <Alert
          type="warning"
          message="Катушка остаётся у клиента"
          description="Долг катушек гасится деньгами, на склад катушка не возвращается. Операция необратима — отменить её из кассы нельзя."
          style={{ marginBottom: 16 }}
        />
        <Form
          key={payHolding?._id}
          form={payForm}
          layout="vertical"
          initialValues={payHolding ? buildPayInitial(payHolding) : undefined}
          onFinish={handlePay}
          onValuesChange={(_, all) => setPayValues(all)}
        >
          <Form.Item
            label="Количество"
            name="quantity"
            rules={[{ required: true, message: "Укажите количество" }]}
          >
            <InputNumber min={1} max={payHolding?.quantity} precision={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Касса"
            name="cashboxId"
            rules={[{ required: true, message: "Выберите кассу" }]}
          >
            <Select placeholder="Выберите кассу" loading={cashboxesLoading}>
              {cashboxesList?.map((cashbox: Cashbox) => (
                <Option key={cashbox._id} value={cashbox._id}>
                  Имя: {cashbox.name} | Валюта: {cashbox.currency}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Курс"
            name="exchangeRate"
            rules={[{ required: true, message: "Укажите курс" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item label="Описание" name="description">
            <Input placeholder="Необязательно" />
          </Form.Item>

          <div style={{ marginBottom: 16 }}>
            <div>
              Списываем долг: <b>{fmt(payDebtAmount)} {currency}</b>
            </div>
            <div>
              Принимаем в кассу:{" "}
              <b>
                {payCashbox ? `${fmt(payCashAmount)} ${payCashbox.currency}` : "— выберите кассу"}
              </b>
            </div>
            <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
              Итоговую сумму считает сервер по залогу этой позиции.
            </div>
          </div>

          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={closePay}>Отмена</Button>
            <Popconfirm
              title="Принять оплату?"
              description="Катушка останется у клиента и на склад не вернётся."
              okText="Принять"
              cancelText="Отмена"
              onConfirm={() => payForm.submit()}
            >
              <Button type="primary" loading={submitting}>
                Принять оплату
              </Button>
            </Popconfirm>
          </Space>
        </Form>
      </Modal>
    </>
  );
};

export default ClientSpoolsModal;
